'use client';
import { useState, useRef, useEffect, useMemo } from 'react';
import DataTable from '../../../components/DataTable';
import { Button, Chip, CircularProgress } from '@mui/material';
import { useMasterUserList, deleteUser, updateUser, buildUserSearchPayload } from '../../../stores/masterUserStore';
import { useCompanyList } from '../../../stores/companyStore';
import ActionMenu from '../../../components/ActionMenu';
import MasterUserAddEdit from './MasterUserAddEdit';
import { useNotifier } from '../../../components/Notifier';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import FormDialog from '../../../components/FormDialog';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { fleetApi, fleetApiGo } from '../../../api/axiosClient';
import CompanyAccesMapDialog from './CompanyAccesMapDialog';
import VehicleAccesMapDialog from './VehicleAccesMapDialog';
import LockConfirmDialog from './LockConfirmDialog';
import { useLanguage } from '../../../utils/i18n';
import { normalizeSearchFieldsConfig, buildAllSearchPayload } from '../../../utils/searchHelper';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

function MasterUserPageContent() {
  const { t } = useLanguage();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [currentUserRole, setCurrentUserRole] = useState('');
  useEffect(() => {
    try {
      const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (raw) {
        const u = JSON.parse(raw);
        setCurrentUserRole(u.role_name || '');
      }
    } catch {}
  }, []);

  const [loadingForm, setLoadingForm] = useState(false);
  const formRef = useRef();
  const notify = useNotifier();
  const { companyOptions } = useCompanyList();
  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });
  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c), // <-- always string
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );
  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (!raw) return;
      let u = null;
      try {
        u = JSON.parse(raw);
      } catch {
        return;
      }
      const companyId = u?.company ?? u?.company_id ?? null;
      if (companyId == null) return;
      const companyIdStr = String(companyId);
      const match = companyOptionsNormalized.find((o) => String(o.value) === companyIdStr);
      const selected = match
        ? [{ value: String(match.value), label: match.label ?? String(match.value) }]
        : [{ value: companyIdStr, label: companyIdStr }];
      setSelectedCompanies((prev) => {
        const prevVal = prev && prev[0] && String(prev[0].value);
        if (prevVal === selected[0].value) return prev;
        return selected;
      });
    } catch (e) {
      console.error('auto select company from user failed', e);
    }
  }, [companyOptionsNormalized]);

  const companyFilterColumns = useMemo(() => {
    let storedCompanyId = null;
    try {
      if (typeof window !== 'undefined') storedCompanyId = localStorage.getItem('companyId');
    } catch {
      storedCompanyId = null;
    }

    if (selectedCompanies && selectedCompanies.length > 0) {
      const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
      if (hasAll) return [];
      const values = selectedCompanies.map((s) => String(s.value));
      if (values.length === 1) {
        return [{ name: 'company_id', logic_operator: '=', operator: 'AND', value: values[0] }];
      } else {
        return [{ name: 'company_id', logic_operator: 'in', operator: 'AND', value: values.join(',') }];
      }
    }

    if (storedCompanyId && String(storedCompanyId) !== 'all') {
      return [{ name: 'company_id', logic_operator: '=', operator: 'AND', value: String(storedCompanyId) }];
    }
    return [];
  }, [selectedCompanies]);
  const [searchField, setSearchField] = useState('username');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [localUsers, setLocalUsers] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localTotal, setLocalTotal] = useState(0);

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'username', label: t('general.username', 'Username') },
      { value: 'fullname', label: t('general.fullName', 'Full Name') },
      { value: 'email', label: t('general.email', 'Email') },
      { value: 'role_name', label: t('general.role', 'Role') }
    ],
    [t]
  );

  const joinDefs = useMemo(() => ({ company: { name: 'company', column_results: ['name'] } }), []);
  const searchFieldsConfig = useMemo(() => normalizeSearchFieldsConfig(searchFieldOptions), [searchFieldOptions]);

  const filterCompanyIds = useMemo(() => {
    if (!selectedCompanies || selectedCompanies.length === 0) return '';
    const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
    if (hasAll) return '';
    return selectedCompanies.map((s) => String(s.value)).join(',');
  }, [selectedCompanies]);

  const {
    data: masterUsersFromHook,
    isLoading: hookLoading,
    totalRecord: hookTotal
  } = useMasterUserList(paginationModel.page, paginationModel.pageSize, searchField, searchRaw, filterCompanyIds);

  const handleFetchAllForExport = async () => {
    try {
      const length = hookTotal > 0 ? hookTotal : 9999;
      const payload = buildUserSearchPayload(0, length, searchField, searchValue, filterCompanyIds);
      const resp = await fleetApiGo.post('/user/search' + getTzParam(), payload);
      const data = resp?.data ?? null;
      return Array.isArray(data) ? data : (data?.data?.data ?? data?.data ?? []);
    } catch (err) {
      console.error('user export all error', err);
      return [];
    }
  };

  const rows = searchField === 'all' ? localUsers : masterUsersFromHook || [];
  const isLoading = searchField === 'all' ? localLoading : hookLoading;
  const totalRecords = searchField === 'all' ? localTotal : hookTotal;

  useEffect(() => {
    const t = setTimeout(() => {
      const v = String(searchRaw ?? '').trim();
      setSearchValue(v);
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);

    return () => clearTimeout(t);
  }, [searchRaw, searchField, companyFilterColumns]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [lockTargetRow, setLockTargetRow] = useState(null);
  const [lockLoading, setLockLoading] = useState(false);
  const [lockAction, setLockAction] = useState('lock'); // 'lock' | 'unlock'

  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [companyDialogRow, setCompanyDialogRow] = useState(null);

  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [vehicleDialogRow, setVehicleDialogRow] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem('companyId');
    if (!stored) return;
    if (companyOptions && companyOptions.length) {
      const match = companyOptions.find((c) => String(c.value) === String(stored));
      if (match) {
        setSelectedCompanies([{ value: String(match.value), label: match.label || match.name || String(match.value) }]);
      } else {
        setSelectedCompanies([{ value: String(stored), label: String(stored) }]);
      }
    }
  }, [companyOptions]);

  useEffect(() => {
    if (searchField !== 'all') return;
    const controller = new AbortController();
    const fetchAllSearch = async () => {
      setLocalLoading(true);
      try {
        const start = paginationModel.page * paginationModel.pageSize;
        const length = paginationModel.pageSize;

        const payload = buildAllSearchPayload({
          q: searchValue,
          searchFieldsConfig,
          joinDefs,
          companyFilterColumns,
          paging: { start, length },
          orders: { columns: ['username'], ascending: true },
          extraJoins: []
        });

        const resp = await fleetApi.post('/user/search' + getTzParam(), payload, { signal: controller.signal });
        const data = resp?.data ?? null;
        const rows = Array.isArray(data) ? data : data?.data || [];
        const total = Array.isArray(data) ? rows.length : (data?.totalRecord ?? data?.totalRecords ?? data?.dataSize ?? 0);
        setLocalUsers(rows);
        setLocalTotal(total);
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Search all error', err);
      } finally {
        if (!controller.signal.aborted) setLocalLoading(false);
      }
    };

    fetchAllSearch();
    return () => controller.abort();
  }, [searchField, searchValue, paginationModel, companyFilterColumns, searchFieldsConfig, joinDefs]);

  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) {
        return prev;
      }
      return model;
    });
  };
  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };
  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };
  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
    setLoadingForm(false);
  };
  const handleSearch = ({ field, value }) => {
    setSearchField(field);
    setSearchRaw(value ?? '');
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };
  const handleDialogSave = () => {
    setLoadingForm(true);
    formRef.current?.submitForm?.();
  };
  const handleCompanyChange = (options) => {
    setSelectedCompanies(options);
  };
  const handleDelete = (id) => {
    setConfirmTargetId(id);
    setConfirmOpen(true);
  };
  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
  };
  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteUser(confirmTargetId);
      notify({ message: t('notify.deleted', 'Deleted successfully'), severity: 'success' });
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.error(err);
      notify({ message: t('notify.deleteFailed', 'Failed to delete'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
    }
  };
  const handleDialogSuccess = (msg) => {
    if (msg) notify({ message: msg, severity: 'success' });
    handleDialogClose();
    setPaginationModel((p) => ({ ...p }));
    setLoadingForm(false);
  };
  const handleDialogError = (msg) => {
    notify({ message: msg || ('notify.operationFailed', 'Operation failed'), severity: 'error' });
    setLoadingForm(false);
  };
  const handleDialogSubmitStart = () => {
    setLoadingForm(true);
  };

  const handleDialogSubmitEnd = () => {
    setLoadingForm(false);
  };

  // ---------- EXTRA ACTIONS: lock / company map / vehicle map ----------
  const handleDialogLockOpen = (row) => {
    const isLocked = Boolean(row?.locked ?? row?.is_locked ?? false);
    setLockTargetRow(row);
    setLockAction(isLocked ? 'unlock' : 'lock');
    setLockDialogOpen(true);
  };

  const handleLockDialogClose = () => {
    if (lockLoading) return;
    setLockDialogOpen(false);
    setLockTargetRow(null);
    setLockAction('lock');
  };

  const handleConfirmLock = async () => {
    if (!lockTargetRow) return;
    setLockLoading(true);
    try {
      const id = lockTargetRow.id ?? lockTargetRow.user_id ?? lockTargetRow.userId;
      if (id == null) throw new Error('Invalid user id');

      const newLockedValue = lockAction === 'lock';
      const companyId = lockTargetRow.company ?? lockTargetRow.co ?? lockTargetRow.company;

      await updateUser(id, {
        locked: Boolean(newLockedValue),
        ...(companyId && { company: companyId })
      });

      notify({
        message: lockAction === 'lock' ? t('notify.userLocked', 'User locked') : t('notify.userUnlocked', 'User unlocked'),
        severity: 'success'
      });
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.error('Lock update error', err);
      notify({ message: t('notify.updateLockFailed', 'Failed to update lock status'), severity: 'error' });
    } finally {
      setLockLoading(false);
      setLockDialogOpen(false);
      setLockTargetRow(null);
      setLockAction('lock');
    }
  };

  const handleCompanyDialogOpen = (row) => {
    setCompanyDialogRow(row);
    setCompanyDialogOpen(true);
  };

  const handleCompanyDialogClose = () => {
    setCompanyDialogOpen(false);
    setCompanyDialogRow(null);
  };

  const handleVehicleDialogOpen = (row) => {
    setVehicleDialogRow(row);
    setVehicleDialogOpen(true);
  };

  const handleVehicleDialogClose = () => {
    setVehicleDialogOpen(false);
    setVehicleDialogRow(null);
  };

  const companyDialogRef = useRef(null);
  const vehicleDialogRef = useRef(null);
  const [companyDialogDirty, setCompanyDialogDirty] = useState(false);
  const [vehicleDialogDirty, setVehicleDialogDirty] = useState(false);
  const [companySaving, setCompanySaving] = useState(false);
  const [vehicleSaving, setVehicleSaving] = useState(false);
  const handleDialogCompanyAccessSave = async () => {
    if (!companyDialogRef.current || typeof companyDialogRef.current.save !== 'function') return;
    setCompanySaving(true);
    try {
      await companyDialogRef.current.save();
      notify({ message: t('notify.assignedCompaniesSaved', 'Assigned companies saved'), severity: 'success' });
      setCompanyDialogOpen(false);
      setCompanyDialogRow(null);
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.error('Save company access error', err);
      notify({ message: err?.message ?? t('notify.failedSave', 'Failed to save'), severity: 'error' });
    } finally {
      setCompanySaving(false);
      setCompanyDialogDirty(false);
    }
  };
  const handleDialogVehicleAccessSave = async () => {
    if (!vehicleDialogRef.current || typeof vehicleDialogRef.current.save !== 'function') return;
    setVehicleSaving(true);
    try {
      await vehicleDialogRef.current.save();
      notify({ message: t('notify.assignedVehicleSaved', 'Assigned vehicle saved'), severity: 'success' });
      setVehicleDialogOpen(false);
      setVehicleDialogRow(null);
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.error('Save vehicle access error', err);
      notify({ message: err?.message ?? t('notify.failedSave', 'Failed to save'), severity: 'error' });
    } finally {
      setVehicleSaving(false);
      setVehicleDialogDirty(false);
    }
  };
  const colorAction = 'primary.900';

  const columns = useMemo(() => {
    const baseCols = [
      {
        field: 'fullname',
        headerName: t('general.fullName', 'Full Name'),
        flex: 0.5,
        valueGetter: (val, row) => row.fullname || row.full_name || ''
      },
      { field: 'username', headerName: t('general.username', 'Username'), flex: 0.5 },
      { field: 'password', headerName: 'Password', flex: 0.5, hide: true, valueGetter: (val, row) => row.password || '' },
      { field: 'email', headerName: t('general.email', 'Email'), flex: 0.5 },
      {
        field: 'company_name',
        headerName: t('general.company', 'Company'),
        flex: 0.5,
        hide: true,
        valueGetter: (val, row) => row.join?.company_name || row.company_name || ''
      },
      { field: 'role_name', headerName: t('general.role', 'Role'), flex: 0.5 },
      {
        field: 'locked',
        headerName: t('general.lockedStatus', 'Locked Status'),
        flex: 0.4,
        valueGetter: (value, params) => {
          const locked = params.locked ?? params.is_locked ?? false;
          return locked ? 'Locked' : 'Unlocked';
        },
        renderCell: (params) => {
          const locked = params.locked ?? params.is_locked ?? false;
          return locked ? (
            <Chip label="Locked" variant="outlined" color="error" size="small" />
          ) : (
            <Chip label="Unlocked" variant="outlined" color="success" size="small" />
          );
        }
      }
    ];

    if (currentUserRole === 'Administrator') {
      baseCols.push(
        { field: 'created_by', headerName: 'Created By', flex: 0.5, hide: true },
        { field: 'created_date', headerName: 'Created Date', flex: 0.5, hide: true },
        { field: 'modified_by', headerName: 'Modified By', flex: 0.5, hide: true },
        { field: 'modified_date', headerName: 'Modified Date', flex: 0.5, hide: true }
      );
    }

    baseCols.push({
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      flex: 0.5,
      headerAlign: 'center',
      renderCell: (params) => {
        const row = params.row || {};
        const locked = Boolean(row?.locked ?? row?.is_locked ?? false);
        const lockIcon = locked ? (
          <LockIcon fontSize="small" sx={{ color: colorAction }} />
        ) : (
          <LockOpenIcon fontSize="small" sx={{ color: colorAction }} />
        );

        return (
          <ActionMenu
            mode="inline"
            row={row}
            onEdit={handleEditClick}
            onDelete={(id) => handleDelete(id, params.row.username)}
            extraActions={[
              {
                label: locked ? t('general.unlockUser', 'Unlock User') : t('general.lockUser', 'Lock User'),
                icon: lockIcon,
                onClick: (r) => handleDialogLockOpen(r)
              },
              {
                label: t('general.companyAccessMap', 'Company Access Map'),
                icon: <AccountBalanceIcon fontSize="small" sx={{ color: colorAction }} />,
                onClick: (r) => handleCompanyDialogOpen(r)
              },
              {
                label: t('general.vehicleAccessMap', 'Vehicle Access Map'),
                icon: <DirectionsCarIcon fontSize="small" sx={{ color: colorAction }} />,
                onClick: (r) => handleVehicleDialogOpen(r)
              }
            ]}
          />
        );
      }
    });

    return baseCols;
  }, [currentUserRole, t]);
  const buildUserTitle = (row, fallback = '') => {
    if (!row) return fallback;
    const name = row.fullname ?? row.full_name ?? row.name ?? row.join?.fullname ?? '';
    const uname = row.username ?? row.user_name ?? row.user_id ?? '';
    if (name && uname) return `${name} — ${uname}`;
    if (name) return `${name}`;
    if (uname) return `${uname}`;
    return fallback;
  };

  return (
    <div>
      <DataTable
        title="Master User"
        rows={rows}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => handleSearch({ field, value })}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
        onFetchAllForExport={handleFetchAllForExport}
      />

      {/* Add/Edit user dialog */}
      <FormDialog
        open={openDialog}
        onClose={handleDialogClose}
        title={editingId ? t('masterUser.edit', 'Edit User') : t('masterUser.add', 'Add User')}
        fullScreenMode={false}
        maxWidth="sm"
        fullHeight={false}
        actions={
          <>
            <Button onClick={handleDialogClose} disableRipple disableElevation>
              {t('buttonTitle.cancel', 'Cancel')}
            </Button>
            <Button
              variant="contained"
              disabled={loadingForm}
              onClick={handleDialogSave}
              sx={{ backgroundColor: '#fff', color: '#000' }}
              startIcon={loadingForm ? <CircularProgress size={18} color="inherit" /> : null} // ADDED: show spinner on Save
            >
              {loadingForm ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
            </Button>
          </>
        }
      >
        <MasterUserAddEdit
          ref={formRef}
          id={editingId}
          companyOptions={companyOptions}
          onClose={handleDialogClose}
          onSuccess={(msg) => handleDialogSuccess(msg)}
          onError={(msg) => handleDialogError(msg)}
          onSubmitStart={handleDialogSubmitStart}
          onSubmitEnd={handleDialogSubmitEnd}
        />
      </FormDialog>

      {/* Delete confirm */}
      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
      />

      {/* Lock Confirm Dialog */}
      <LockConfirmDialog
        open={lockDialogOpen}
        action={lockAction}
        loading={lockLoading}
        itemName={lockTargetRow ? (lockTargetRow.username ?? lockTargetRow.id) : ''}
        onClose={handleLockDialogClose}
        onConfirm={handleConfirmLock}
        confirmLabel={t('general.yes', 'Yes')}
        cancelLabel={t('general.no', 'No')}
      />

      {/* Company Access Map dialog */}
      <FormDialog
        open={companyDialogOpen}
        onClose={handleCompanyDialogClose}
        title={
          t('general.companyAccessMap', 'Company Access Map') +
          ' | ' +
          buildUserTitle(companyDialogRow, t('general.companyAccessMap', 'Company Access Map'))
        }
        fullScreenMode={true}
        fullHeight={true}
        actions={
          <>
            <Button
              variant="contained"
              disabled={!companyDialogDirty || companySaving}
              onClick={handleDialogCompanyAccessSave}
              sx={{ backgroundColor: '#0072DA' }}
            >
              {companySaving ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
            </Button>
            <Button onClick={handleCompanyDialogClose} disableRipple disableElevation variant="outlined">
              {t('buttonTitle.cancel', 'Cancel')}
            </Button>
          </>
        }
      >
        <CompanyAccesMapDialog ref={companyDialogRef} userRow={companyDialogRow} onDirtyChange={setCompanyDialogDirty} />
      </FormDialog>

      {/* Vehicle Access Map dialog */}
      <FormDialog
        open={vehicleDialogOpen}
        onClose={handleVehicleDialogClose}
        title={
          t('general.vehicleAccessMap', 'Vehicle Access Map') +
          ' | ' +
          buildUserTitle(vehicleDialogRow, t('general.vehicleAccessMap', 'Vehicle Access Map'))
        }
        fullScreenMode={true}
        fullHeight={true}
        actions={
          <>
            <Button
              variant="contained"
              disabled={!vehicleDialogDirty || vehicleSaving}
              onClick={handleDialogVehicleAccessSave}
              sx={{ backgroundColor: '#0072DA' }}
            >
              {vehicleSaving ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
            </Button>
            <Button onClick={handleVehicleDialogClose} disableRipple disableElevation variant="outlined">
              {t('buttonTitle.cancel', 'Cancel')}
            </Button>
          </>
        }
      >
        <VehicleAccesMapDialog userRow={vehicleDialogRow} ref={vehicleDialogRef} onDirtyChange={setVehicleDialogDirty} />
      </FormDialog>
    </div>
  );
}

export default function MasterUserPage() {
  return (
    <>
      <MasterUserPageContent />
    </>
  );
}
